/**
 * Which enquiry type a list of requested items belongs to (Akhil, 2026-09-21) — for an enquiry
 * created without a person to choose. The type scopes item search on the RFQ builder, so a wrong
 * one narrows the catalogue for the whole enquiry; it is decided from the items themselves.
 *
 * The model is asked to pick ONE of the server's `Enquiry Type` values (Provision · Technical ·
 * Service on the bench) and its answer is accepted only if it is one of them. Without the `/llm`
 * proxy, or when the model answers nonsense, a keyword vote keeps provisions apart from
 * technical stores — coarse, but never a value the server would reject.
 */
import { currentClerkToken } from './api/frappeSession'

const LLM_PATH = '/llm'
const LLM_MODEL: string = (import.meta.env?.VITE_BOSUN_LLM_MODEL as string | undefined) ?? 'deepseek/deepseek-v3.2-exp'

export function typePrompt(descriptions: readonly string[], types: readonly string[]): string {
  return [
    'You classify a ship-chandler enquiry from the items a vessel is asking for.',
    `Answer with exactly one word from this list and nothing else: ${types.join(' | ')}.`,
    '',
    'Definitions:',
    '- Provision: FOOD and soft DRINK only — fresh, frozen, dry and canned food, meat, fish, vegetables, fruit, dairy, bakery, juice, water, tea, coffee. Nothing else is Provision.',
    '- Technical: every physical item that is not food or drink — engine and deck spares, tools, chemicals, paint, ropes, safety gear, electrical, electronics, stationery, galley equipment, cleaning materials, cabin and bonded stores.',
    '- Service: work to be carried out rather than goods supplied — repairs, surveys, inspections, servicing, calibration, crew transport.',
    '',
    'Rules: if the items are a mix, answer the type of the MAJORITY by line count. A cooking pot, a cleaning chemical, a galley appliance, toiletries, and bonded stores (alcohol, tobacco) are Technical, not Provision. Answer the single word only.',
    '',
    'Items:',
    ...descriptions.slice(0, 80).map((d) => `- ${d}`),
  ].join('\n')
}

export function parseTypeAnswer(answer: string, types: readonly string[]): string | null {
  const a = answer.toLowerCase()
  // The longest listed type that appears, so "Bonded Stores" cannot match "Stores" alone.
  const hits = types.filter((t) => a.includes(t.toLowerCase())).sort((x, y) => y.length - x.length)
  return hits[0] ?? null
}

const PROVISION = /\b(rice|flour|eggs?|sugar|salt|oil\s*(?:cooking|sunflower|olive)|egg|milk|cheese|butter|chicken|beef|mutton|lamb|fish|prawn|shrimp|vegetable|onion|potato|tomato|fruit|apple|banana|bread|biscuit|juice|water|tea|coffee|frozen|canned|jam|noodle|pasta|cereal|yoghurt|spice|provision)\b/i
const TECHNICAL = /\b(oil|grease|gasket|valve|pump|filter|bearing|bolt|nut|rope|wire|cable|paint|chemical|battery|lamp|bulb|welding|gas|tool|spanner|hose|pipe|flange|seal|belt|motor|spare|hydraulic|engine|deck|safety|helmet|glove|extinguisher|thermometer|meter|sensor)\b/i

export function classifyByKeywords(descriptions: readonly string[], types: readonly string[]): string | null {
  let provision = 0
  let technical = 0
  for (const d of descriptions) {
    if (PROVISION.test(d)) provision += 1
    else if (TECHNICAL.test(d)) technical += 1
  }
  const pick = provision > technical ? 'Provision' : technical > 0 ? 'Technical' : null
  return pick && types.includes(pick) ? pick : null
}

async function authHeaders(): Promise<Record<string, string>> {
  const bearer = await currentClerkToken()
  return bearer ? { Authorization: `Bearer ${bearer}` } : {}
}

/** The model's pick, else the keyword vote, else `null` — the caller then leaves the type unset. */
export async function classifyEnquiryType(descriptions: readonly string[], types: readonly string[]): Promise<string | null> {
  if (descriptions.length === 0 || types.length === 0) return null
  try {
    const res = await fetch(LLM_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ model: LLM_MODEL, temperature: 0, max_tokens: 20, messages: [{ role: 'user', content: typePrompt(descriptions, types) }] }),
    })
    if (res.ok) {
      const json = (await res.json()) as { choices?: Array<{ message?: { content?: unknown } }> }
      const answer = String(json.choices?.[0]?.message?.content ?? '')
      const parsed = parseTypeAnswer(answer, types)
      if (parsed) return parsed
    }
  } catch {
    // No proxy, or a network failure — the keyword rule below answers instead.
  }
  return classifyByKeywords(descriptions, types)
}
